import {
  DragEndEvent,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { useIntroducerContext } from 'contexts/IntroducerContext/IntroducerContext';
import { useAmplitude } from 'hooks/useAmplitude';
import { TApplication } from 'lendingApi/applications.types';
import _ from 'lodash';
import { useNavigate } from 'react-router-dom';
import { checkApplicationIsComplete } from 'utils/application/application.helpers';
import { APPLICATION_STATUS } from 'utils/applications/applications.config';
import { PATH_NAMES } from 'utils/routes/pathNames';
import { AMPLITUDE_EVENTS } from 'utils/tracking/tracking.events';
import { useContinueRedirect } from './Cells/ApplicationStatusCell/useContinueRedrect.hook';
import { useApplicationsTableState } from './Context/ApplicationsTableState.context';

export const useApplicationsTable = () => {
  const navigate = useNavigate();
  const { trackEvent } = useAmplitude();
  const { handleRedirect } = useContinueRedirect();
  const { introducerType, checkIntroducerPermission } = useIntroducerContext();

  const { columnOrder, headerGroups, rows, setColumnOrder, table } =
    useApplicationsTableState();

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over) return;

    const overColumn = table.getColumn(over.id.toString());

    // if the target column is not draggable, cancel move
    if (overColumn?.columnDef.meta?.draggable === false) return;

    const activeIndex = columnOrder.indexOf(active.id.toString());
    const overIndex = columnOrder.indexOf(over.id.toString());

    const newOrder = [...columnOrder];
    newOrder.splice(activeIndex, 1);
    newOrder.splice(overIndex, 0, active.id.toString());

    setColumnOrder(newOrder);

    trackEvent(AMPLITUDE_EVENTS.columnReorder, {
      Column: active.id.toString(),
    });
  };

  const sensors = useSensors(
    useSensor(MouseSensor, {}),
    useSensor(TouchSensor, {}),
    useSensor(KeyboardSensor, {}),
  );

  const handleRowClick = (application: TApplication) => {
    const canSubmitPartnerApps =
      introducerType === 'partner' &&
      checkIntroducerPermission('can_create_applications');

    trackEvent(AMPLITUDE_EVENTS.dashboardRowClicked, {
      Status: _.capitalize(_.startCase(application.status)),
      Channel: application.is_partner_channel ? 'Partner' : 'Broker',
    });
    const stateKey = application.customer?.state_key;
    const applicationStatus = application.status;

    const isPartnerChannel = application.is_partner_channel;

    if (isPartnerChannel && !canSubmitPartnerApps) {
      return navigate(
        PATH_NAMES.application({ applicationId: application.application_uid }),
      );
    }

    if (applicationStatus === 'open') {
      if (checkApplicationIsComplete(application)) {
        return navigate(
          PATH_NAMES.applicationDocuments({
            applicationId: application.application_uid,
          }),
        );
      }

      return handleRedirect(stateKey, application.application_uid);
    }

    if (applicationStatus === APPLICATION_STATUS.action_required) {
      return navigate(
        PATH_NAMES.applicationDocuments({
          applicationId: application.application_uid,
        }),
      );
    }

    navigate(
      PATH_NAMES.application({ applicationId: application.application_uid }),
    );
  };

  return {
    headerGroups,
    rows,
    sensors,
    table,
    columnOrder,
    handleDragEnd,
    handleRowClick,
  };
};
